import moment from "moment";
import * as React from "react";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router";
import { bindActionCreators } from "redux";
import ToggleButton from "src/components/common/Buttons/ToggleButton";
import Tree, { ITree } from "src/components/common/Tree/Tree";
import { TrialInstitutionPersonnelsBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { TrialInstitutionSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as TrialInstitutionActions from "../../../actions/trialInstitution";
import * as TrialPersonnelActions from "../../../actions/trialPersonnel";
import { InlineLoading } from "../../../components/common/index";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { ITmdState } from "../../../reducers/index";
import { TrialInstitutionLayout } from "../../index";

import "./TrialInstitutionPersonnelsPage.scss";

interface TrialInstitutionPersonnelPageProps {
    trialInstitution: Dtos.TrialInstitution;
    loadingTrialInstitution: boolean;
    loadTrialInstitutionSuccess: boolean;
    loadTrialInstitutionFailure: boolean;
    loadTrialInstitutionErrorCode: string;
    trialPersonnel: Dtos.TrialPersonnel[];
    loadingTrialPersonnel: boolean;
    loadTrialPersonnelSuccess: boolean;
    loadTrialPersonnelFailure: boolean;
    loadTrialPersonnelErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearTrialInstitution: TrialInstitutionActions.ITrialInstitutionClearActionCreator;
    loadTrialInstitution: TrialInstitutionActions.ITrialInstitutionLoadByIdActionCreator;
    clearTrialPersonnel: TrialPersonnelActions.ITrialPersonnelClearActionCreator;
    loadTrialPersonnel: TrialPersonnelActions.ITrialPersonnelsLoadByTrialInstitutionIdActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const TrialInstitutionPersonnelsPage: FC<
    TrialInstitutionPersonnelPageProps
> = ({
    trialInstitution,
    loadingTrialInstitution,
    loadTrialInstitutionSuccess,
    loadTrialInstitutionFailure,
    loadTrialInstitutionErrorCode,
    trialPersonnel,
    loadingTrialPersonnel,
    loadTrialPersonnelSuccess,
    loadTrialPersonnelFailure,
    loadTrialPersonnelErrorCode,
    permissions,
    clearTrialInstitution,
    loadTrialInstitution,
    clearTrialPersonnel,
    loadTrialPersonnel,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { trialId } = useParams<{ trialId: string }>();
    const { trialInstitutionId } = useParams<{ trialInstitutionId: string }>();
    const trialIdNumber = parseInt(trialId, 10);
    const trialInstitutionIdNumber = parseInt(trialInstitutionId, 10);

    //
    // -- States --
    const [showCeased, setShowCeased] = useState<boolean>(false);
    const [treeData, setTreeData] = useState<ITree | null>(null);
    const [initalizedTreeData, setInitalizedTreeData] = useState<boolean>(false);
    const [toggleState, setToggleState] = useState<string[]>([]);
    const [creatingTree, setCreatingTree] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- Variables --
    const showTrialInstitutionPersonnels =
        !loadingTrialInstitution &&
        loadTrialInstitutionSuccess &&
        !loadingTrialPersonnel &&
        loadTrialPersonnelSuccess &&
        !creatingTree;

    
    // Load Trial Institution & Personnel
    useEffect(() => {
        if (trialInstitutionIdNumber && !trialInstitution) {
            setBreadcrumbs(
                TrialInstitutionPersonnelsBreadcrumbs(
                    trialIdNumber,
                    trialInstitutionIdNumber
                )
            );

            setTreeData(null);

            loadTrialInstitution(trialInstitutionIdNumber);
            loadTrialPersonnel(trialInstitutionIdNumber, undefined);
        }
    }, [
        trialIdNumber,
        trialInstitutionIdNumber,
        trialInstitution,
        permissions,
        setBreadcrumbs,
        loadTrialInstitution,
        loadTrialPersonnel,
        TrialInstitutionPersonnelsBreadcrumbs,
        setTreeData
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialInstitutionFailure) {
            navigate(`/error/${loadTrialInstitutionErrorCode}`);
        }
        if (loadTrialPersonnelFailure) {
            navigate(`/error/${loadTrialPersonnelErrorCode}`);
        }
    }, [
        loadTrialInstitutionFailure, 
        loadTrialInstitutionErrorCode, 
        loadTrialPersonnelFailure, 
        loadTrialPersonnelErrorCode, 
        navigate
    ]);

    // Update breadcrumbs & submenu
    useEffect(() => {
        if (trialInstitutionIdNumber && trialInstitution) {
            setDrawerItems(
                TrialInstitutionSubMenu(trialInstitution, permissions)
            );
            if (
                trialInstitution?.trial?.displayName &&
                trialInstitution?.institution?.name
            ) {
                setBreadcrumbs(
                    TrialInstitutionPersonnelsBreadcrumbs(
                        trialIdNumber,
                        trialInstitutionIdNumber,
                        trialInstitution?.trial.displayName,
                        trialInstitution.institution.name
                    )
                );
            }
        }
    }, [
        trialInstitution,
        trialInstitutionIdNumber,
        permissions,
        setDrawerItems,
        setBreadcrumbs,
        TrialInstitutionSubMenu,
        TrialInstitutionPersonnelsBreadcrumbs,
    ]);

    // Cleanup effect to clear the trial institution on component unmount
    useEffect(() => {
        return () => {
            setTreeData(null);
            clearTrialInstitution();
            clearTrialPersonnel();
        };
    }, [clearTrialInstitution, clearTrialPersonnel, setTreeData]);

    // Refresh tree if trialpersonnel changes
    useEffect(() => {
        setTreeData(null);
        setInitalizedTreeData(false);
    }, [trialPersonnel]);

    // Create tree
    useEffect(() => {
        if (trialPersonnel && !initalizedTreeData) {
            setCreatingTree(true);

            // If no trial institution personnel return
            if (trialPersonnel?.length == 0) {
                setCreatingTree(false);
                return;
            }

            let uniqueRoles: Dtos.Role[] = [];
            if (showCeased) {
                uniqueRoles = Array.from(
                    new Map(
                        trialPersonnel?.map((x) => [
                            x.role.id,
                            x.role,
                        ])
                    ).values()
                );
            } else {
                uniqueRoles = Array.from(
                    new Map(
                        trialPersonnel
                            ?.filter((x) => !x.dateCeased)
                            .map((x) => [x.role.id, x.role])
                    ).values()
                );
            }

            const createChildNodes = (roleId, showCeased) => {
                let children = [];

                if (showCeased) {
                    children =
                        trialPersonnel
                            ?.filter((y) => y.roleId == roleId)
                            ?.sort((a, b) => a?.id - b?.id)
                            ?.map((y) => ({
                                id: `personnel-${y.id}`,
                                data: (
                                    <div
                                        className={`form-row ${
                                            y?.dateCeased ? "ceased" : ""
                                        }`}
                                    >
                                        <div className="col-1">
                                            <FontAwesomeIcon
                                                icon={
                                                    FontAwesomeIcons.Duotone
                                                        .USER
                                                }
                                            />
                                        </div>
                                        <div className="col">
                                            {
                                                y?.personnel
                                                    ?.displayWithSalutation
                                            }
                                        </div>
                                        <div className="col">
                                            {y?.dateStarted
                                                ? moment(
                                                        y?.dateStarted,
                                                        "YYYY-MM-DD HH:mm:ss.SSSSSSS Z"
                                                    ).format("DD/MM/YYYY")
                                                : ""}
                                        </div>
                                        <div className="col">
                                            {y?.dateCeased
                                                ? moment(
                                                        y?.dateCeased,
                                                        "YYYY-MM-DD HH:mm:ss.SSSSSSS Z"
                                                    ).format("DD/MM/YYYY")
                                                : ""}
                                        </div>
                                    </div>
                                ),
                                icon: null,
                                collapsible: false,
                            })) ?? [];
                } else {
                    children =
                        trialPersonnel
                            ?.filter((y) => y.roleId == roleId && !y.dateCeased)
                            ?.sort((a, b) => a?.id - b?.id)
                            ?.map((y) => ({
                                id: `personnel-${y?.id}`,
                                data: (
                                    <div className="form-row">
                                        <div className="col-1">
                                            <FontAwesomeIcon
                                                icon={
                                                    FontAwesomeIcons.Duotone
                                                        .USER
                                                }
                                            />
                                        </div>
                                        <div className="col">
                                            {
                                                y?.personnel
                                                    ?.displayWithSalutation
                                            }
                                        </div>
                                        <div className="col">
                                            {y?.dateStarted
                                                ? moment(
                                                        y?.dateStarted,
                                                        "YYYY-MM-DD HH:mm:ss.SSSSSSS Z"
                                                    ).format("DD/MM/YYYY")
                                                : ""}
                                        </div>
                                        <div className="col">
                                            {y?.dateCeased
                                                ? moment(
                                                        y?.dateCeased,
                                                        "YYYY-MM-DD HH:mm:ss.SSSSSSS Z"
                                                    ).format("DD/MM/YYYY")
                                                : ""}
                                        </div>
                                    </div>
                                ),
                                icon: null,
                                collapsible: false,
                            })) ?? [];
                }

                // Add table header
                if (children?.length > 0) {
                    children.unshift({
                        id: `tableheader`,
                        data: (
                            <div className="form-row">
                                <div className="col-1"></div>
                                <div className="col">
                                    <strong>Name</strong>
                                </div>
                                <div className="col">
                                    <strong>Date Started</strong>
                                </div>
                                <div className="col">
                                    <strong>Date Ceased</strong>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: false,
                    });
                }

                return children;
            };

            let treeNodes =
                uniqueRoles
                    ?.sort((a, b) => a?.value?.localeCompare(b?.value))
                    ?.map((x) => ({
                        id: `role-${x.id}`,
                        data: (
                            <div className="d-flex align-items-center">
                                <FontAwesomeIcon
                                    icon={FontAwesomeIcons.Duotone.ROLE}
                                />
                                <span
                                    className="pl-4 mb-0"
                                    style={{ width: "275px" }}
                                >
                                    {x?.value}
                                </span>
                                <div
                                    className="d-flex align-items-center justify-content-center"
                                    style={{ width: "50px" }}
                                >
                                    <span className="badge badge-pill badge-secondary ml-4 mb-0">
                                        {
                                            trialPersonnel?.filter(
                                                (y) =>
                                                    showCeased
                                                        ? y.roleId == x.id
                                                        : y.roleId == x.id &&
                                                            !y.dateCeased
                                            )?.length
                                        }
                                    </span>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: true,
                        subTree: {
                            children: [...createChildNodes(x.id, showCeased)],
                        },
                    })) ?? [];

            // Initialize tree toggle
            const expanded = treeNodes.map((z) => z.id);

            setToggleState(expanded);
            setTreeData({ children: treeNodes });
            setInitalizedTreeData(true);
            setCreatingTree(false);
        }
    }, [
        trialPersonnel,
        initalizedTreeData,
        treeData,
        setTreeData,
        setToggleState,
        setInitalizedTreeData,
        showCeased,
        setCreatingTree,
    ]);

    const handleToggle = useCallback(
        (id: string, navigateFlag?: boolean) => {
            if (navigateFlag) {
                if (
                    id.includes("personnel") &&
                    AuthenticationHelper.hasPermission(
                        permissions,
                        Dtos.Permission.TrialPersonnelRoleView
                    )
                ) {
                    navigate(
                        `/trials/${trialIdNumber}/trialinstitution/${trialInstitutionIdNumber}/personnel/${id.replace(
                            "personnel-",
                            ""
                        )}/details`
                    );
                }
                if (id.includes("role")) {
                    setToggleState((prev) =>
                        prev.includes(id)
                            ? prev.filter((nodeId) => nodeId !== id)
                            : [...prev, id]
                    );
                }
            } else {
                setToggleState((prev) =>
                    prev.includes(id)
                        ? prev.filter((nodeId) => nodeId !== id)
                        : [...prev, id]
                );
            }
        },
        [
            setToggleState,
            navigate,
            trialIdNumber,
            trialInstitutionIdNumber,
            permissions,
        ]
    );

    const onChange = (newValue) => {
        setInitalizedTreeData(false);
        setShowCeased(newValue);
    };

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className="col-md-12 col-lg-auto mb-2">
                        <h1>Trial Institution Personnel</h1>
                    </div>
                    <div
                        className="col-md-12 col-lg d-flex justify-content-md-start justify-content-lg-end align-items-stretch mb-2 mb-lg-0"
                        style={{ height: "38px" }}
                    >
                        {trialInstitution &&
                            trialInstitution.id > 0 &&
                            AuthenticationHelper.hasPermission(
                                permissions,
                                Dtos.Permission.TrialPersonnelRoleCreate,
                                trialInstitution?.trialId,
                                trialInstitution?.institutionId
                            ) && (
                                <>
                                    <ToggleButton
                                        id="toggle-button-ceased"
                                        checked={showCeased}
                                        onChange={onChange}
                                        labelA="Hide ceased personnel"
                                        labelB="Show ceased personnel"
                                        style={{ minWidth: "200px" }}
                                    />
                                    <a
                                        className="btn btn-info mr-2"
                                        target="_blank"
                                        href={encodeURI(
                                            `/api/personnelrole/export/${trialInstitution?.trialId?.toString()}/${trialInstitution?.institutionId?.toString()}/0/0`
                                        )}
                                    >
                                        <FontAwesomeIcon icon={FontAwesomeIcons.Solid.CLOUD_DOWNLOAD} fixedWidth />
                                    </a>
                                    <Link
                                        className={"btn btn-primary"}
                                        url={`/trials/${trialIdNumber}/trialinstitution/${trialInstitutionIdNumber}/personnel/create`}
                                    >
                                        Create
                                    </Link>
                                </>
                            )}
                    </div>
                </div>
                {!showTrialInstitutionPersonnels ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showTrialInstitutionPersonnels ? (
                    <>
                        {treeData && treeData?.children?.length > 0 ? (
                            <Tree
                                treeData={treeData}
                                onToggle={handleToggle}
                                toggleState={toggleState}
                            />
                        ) : (
                            <div className="card card-shadow p-3">
                                <div className="alert alert-info d-flex mb-0">
                                    <div className="mr-2">
                                        <FontAwesomeIcon
                                            icon={
                                                FontAwesomeIcons.Solid
                                                    .INFO_SQUARE
                                            }
                                            fixedWidth
                                        />
                                    </div>
                                    <span>
                                        There are currently no trial institution
                                        personnel.
                                    </span>
                                </div>
                            </div>
                        )}
                    </>
                ) : null}
            </div>
        );
    }, [
        trialInstitution,
        permissions,
        showTrialInstitutionPersonnels,
        showCeased,
        treeData,
        handleToggle,
        toggleState,
    ]);

    return (
        <TrialInstitutionLayout
            loading={loadingTrialInstitution || loadingTrialPersonnel}
            permission={Dtos.Permission.TrialPersonnelRoleView}
        >
            {renderContent}
        </TrialInstitutionLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<TrialInstitutionPersonnelPageProps> => {
    let trialInstitution: Dtos.TrialInstitution | undefined = !(
        state.trialInstitution.formData instanceof Array
    )
        ? state.trialInstitution.formData
        : undefined;
    let trialPersonnel: Dtos.TrialPersonnel[] | undefined = (state.trialPersonnel.formData instanceof Array)
        ? state.trialPersonnel.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        trialInstitution: trialInstitution!,
        loadingTrialInstitution:
            state.trialInstitution.loadState &&
            state.trialInstitution.loadState.status == RequestState.Pending,
        loadTrialInstitutionSuccess:
            state.trialInstitution.loadState &&
            state.trialInstitution.loadState.status == RequestState.Success,
        loadTrialInstitutionFailure:
            state.trialInstitution.loadState &&
            state.trialInstitution.loadState.status == RequestState.Failure,
        loadTrialInstitutionErrorCode:
            state.trialInstitution.loadState.errorCode,
        trialPersonnel: trialPersonnel!,
        loadingTrialPersonnel:
            state.trialPersonnel.loadState &&
            state.trialPersonnel.loadState.status == RequestState.Pending,
        loadTrialPersonnelSuccess:
            state.trialPersonnel.loadState &&
            state.trialPersonnel.loadState.status == RequestState.Success,
        loadTrialPersonnelFailure:
            state.trialPersonnel.loadState &&
            state.trialPersonnel.loadState.status == RequestState.Failure,
        loadTrialPersonnelErrorCode:
            state.trialInstitution.loadState.errorCode,
        permissions: permissions,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<TrialInstitutionPersonnelPageProps> => {
    return {
        loadTrialInstitution: bindActionCreators(
            TrialInstitutionActions.LoadTrialInstitutionById,
            dispatch
        ),
        clearTrialInstitution: bindActionCreators(
            TrialInstitutionActions.Clear,
            dispatch
        ),
        loadTrialPersonnel: bindActionCreators(
            TrialPersonnelActions.LoadTrialPersonnelsByTrialInstitutionId,
            dispatch
        ),
        clearTrialPersonnel: bindActionCreators(
            TrialPersonnelActions.Clear,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(TrialInstitutionPersonnelsPage);
